import { createFileRoute, redirect } from "@tanstack/react-router";
import { Building2, CalendarClock, Globe, SlidersHorizontal, Users } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

import { PageShell } from "@/components/layouts/shells/page-shell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUser } from "@/features/auth/hooks/use-user";
import { AvailabilityTab } from "@/features/booking/modules/availability/availability-tab";
import { SettingsTab } from "@/features/booking/modules/settings/settings-tab";
import { SetupActionsSlot } from "@/features/booking/modules/setup-actions/setup-actions";
import { TeamTab } from "@/features/booking/modules/team/team-tab";
import { PublicPageTab } from "@/features/trainer-page/modules/public-page-tab/public-page-tab";
import { m } from "@/paraglide/messages.js";

const SETUP_TABS = ["availability", "team", "business", "page"] as const;
type SetupTab = (typeof SETUP_TABS)[number];

export const Route = createFileRoute("/(private)/booking/setup/")({
  loader: () => ({ crumb: m.booking_setup_page_title() }),
  // "services" moved to the offer (ADR 0035): old links land there, on a new one if they asked for it.
  validateSearch: z.object({
    tab: z.enum([...SETUP_TABS, "services"]).optional(),
    bookingId: z.string().optional(),
    create: z.enum(["visit", "subscription"]).optional().catch(undefined),
  }),
  beforeLoad: ({ search }) => {
    if (search.tab === "services") throw redirect({ to: "/offer", search: { create: search.create }, replace: true });
  },
  component: SetupPage,
});

const isSetupTab = (value: string): value is SetupTab => (SETUP_TABS as readonly string[]).includes(value);

/**
 * The things you set once and revisit rarely: when you work, who takes bookings and how the business
 * runs. What it sells is in the offer. Everyone can read it; only admins change business settings.
 * Availability is everyone's own, so anyone edits theirs.
 */
function SetupPage() {
  const { hasPermission } = useUser();
  const canEdit = hasPermission("manage", "booking_settings");
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const active = tab && isSetupTab(tab) ? tab : "availability";
  const [actionsNode, setActionsNode] = useState<HTMLElement | null>(null);

  const setTab = (value: string) => {
    if (isSetupTab(value)) navigate({ to: "/booking/setup", search: { tab: value }, replace: true });
  };

  return (
    <PageShell
      icon={SlidersHorizontal}
      title={m.booking_setup_page_title()}
      description={m.booking_setup_page_description()}
    >
      <SetupActionsSlot.Provider value={actionsNode}>
        <Tabs value={active} onValueChange={setTab} className="gap-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <TabsList data-tour="booking-setup-tabs">
              <TabsTrigger value="availability" data-tour="booking-setup-availability">
                <CalendarClock aria-hidden />
                {m.booking_setup_tab_availability()}
              </TabsTrigger>
              <TabsTrigger value="team" data-tour="booking-setup-team">
                <Users aria-hidden />
                {m.booking_setup_tab_team()}
              </TabsTrigger>
              <TabsTrigger value="business" data-tour="booking-setup-business">
                <Building2 aria-hidden />
                {m.booking_setup_tab_business()}
              </TabsTrigger>
              <TabsTrigger value="page" data-tour="booking-setup-page">
                <Globe aria-hidden />
                {m.booking_setup_tab_page()}
              </TabsTrigger>
            </TabsList>
            <div ref={setActionsNode} className="flex shrink-0 gap-2 empty:hidden" />
          </div>
          <TabsContent value="availability">
            <AvailabilityTab />
          </TabsContent>
          <TabsContent value="team">
            <TeamTab canEdit={canEdit} />
          </TabsContent>
          <TabsContent value="business">
            <SettingsTab canEditBusiness={canEdit} />
          </TabsContent>
          <TabsContent value="page">
            <PublicPageTab canEdit={canEdit} />
          </TabsContent>
        </Tabs>
      </SetupActionsSlot.Provider>
    </PageShell>
  );
}
